import React, { Fragment, useEffect, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Form, getFormValues, hasSubmitSucceeded, initialize, isDirty, reduxForm, SubmissionError } from 'redux-form';
import { PageDecorated } from 'pages/page';
import { isNil, noop } from 'lodash-es';
import { deleteVideoAction } from 'common/actions';
import {
  FormControlsDecorated,
  myVideoRequestAction,
  myVideosSelectedVideoSelector,
  resetMyVideosAction,
  VideoPreviewHeader,
} from 'pages/my-videos.page';
import { publishVideoRequestAction, uploadFormConfigRequestAction, uploadResetAction } from 'pages/upload.page/actions';
import { uploadFormConfigSelector } from 'pages/upload.page/selectors';
import { videoFormTransform, videoFormValidate } from 'components/forms';
import { channelsSelector, getRatingFeatureSelector, websiteSelector } from 'common/selectors';
import { ContentBox } from 'components/content-box';
import { BlockUi } from 'components/block-ui';
import { LoadingSpinner } from 'components/loading-spinner';
import { FormFields } from 'pages/my-videos.page/edit.page/fragments/form-fields';
import { GenerateThumbnailDialog, ThumbnailDecorated } from 'pages/my-videos.page/edit.page/fragments/thumbnail';
import { VideoTab } from 'components/video-box/video-tabs/fragments/video-tab';
import { myVideosEditFormState } from 'pages/my-videos.page/selectors';
import { useThumbnailUrl } from 'components/video-generating/use-thumbnail-url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';

export const EDIT_MY_VIDEO_FORM = 'editMyVideoForm';

const mapDispatchToProps = (dispatch, props) => ({
  fetchFormConfig: () => dispatch(uploadFormConfigRequestAction()),

  fetchVideo: () => props.match.params.videoId && dispatch(myVideoRequestAction(props.match.params.videoId)),

  updateVideo: (video, formValues) => {
    dispatch(publishVideoRequestAction(video));

    dispatch(initialize(EDIT_MY_VIDEO_FORM, formValues));
  },

  deleteVideo: (videoId) => dispatch(deleteVideoAction(videoId)),

  resetMyVideos: () => {
    dispatch(resetMyVideosAction());

    dispatch(uploadResetAction());
  },
});

const mapStateToProps = (state) => ({
  channels: channelsSelector(state),
  videoFormConfig: uploadFormConfigSelector(state),
  myVideo: myVideosSelectedVideoSelector(state),
  website: websiteSelector(state),
  withRating: getRatingFeatureSelector(state),
  initialValues: myVideosEditFormState(state),
  formValues: getFormValues(EDIT_MY_VIDEO_FORM)(state),
  isDirty: isDirty(EDIT_MY_VIDEO_FORM)(state),
  formSubmitted: hasSubmitSucceeded(EDIT_MY_VIDEO_FORM)(state),
});

const EditMyVideoPage = (props) => {
  const { fetchFormConfig, fetchVideo, resetMyVideos, videoFormConfig, myVideo, website, withRating, formValues, t } =
    props;

  const [showForm, setShowForm] = useState(true);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const thumbnailUrl = useThumbnailUrl(myVideo);

  const metadataTabRef = useRef(null);
  const thumbnailTabRef = useRef(null);

  const toggleTabFocus = () => {
    thumbnailTabRef.current === document.activeElement
      ? metadataTabRef.current.focus()
      : thumbnailTabRef.current.focus();
  };

  const handleKeyDown = (event) => {
    let flag = false;

    switch (event.key) {
      case 'Home':
      case 'End':
      case 'ArrowRight':
      case 'ArrowLeft':
        if (thumbnailTabRef.current && metadataTabRef.current) {
          // We can toggle focus, because we have max 2 tabs
          toggleTabFocus();

          flag = true;
        }
        break;
      default:
        break;
    }

    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  useEffect(() => {
    fetchFormConfig();
    fetchVideo();

    return () => resetMyVideos();
  }, [fetchFormConfig, fetchVideo, thumbnailUrl, resetMyVideos]);

  const submitChanges = (formData) => {
    const { formValues, videoFormConfig, updateVideo, myVideo } = props;

    return new Promise((resolve, reject) => {
      const errors = videoFormValidate(videoFormConfig, formData);

      if (Object.keys(errors).length > 0) {
        reject(new SubmissionError(errors));
      } else {
        updateVideo(videoFormTransform(videoFormConfig, formData, myVideo.id), formValues);
        resolve();
      }
    });
  };

  const onBack = () => {
    let shouldBack = true;

    if (props.isDirty && !props.formSubmitted) {
      shouldBack = window.confirm(props.t('edit_my_video_back'));
    }

    if (shouldBack) {
      props.history.push(`/my-videos/1/${props.myVideo.id}#top`);
    }
  };

  const switchTabs = () => {
    setShowForm(!showForm);
    setShowThumbnail(!showThumbnail);
  };

  const showBlockUi = isNil(videoFormConfig) || isNil(myVideo);

  if (showBlockUi) {
    return false;
  }

  return (
    <PageDecorated>
      <div className="container p-0 p-sm-2">
        <div className="ct-upload-step-2 ct-edit-my-video-page" data-e2e="edit-my-video-page">
          <ContentBox className="ct-edit-my-video-page--ct-wrapper">
            <span className="ct-edit-my-video-page__back" onClick={onBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('back')}
            </span>

            <VideoPreviewHeader
              playerConfiguration={website.playerConfiguration}
              video={myVideo}
              withRating={withRating}
              thumbnailUrl={thumbnailUrl}
            />
          </ContentBox>

          <div className="row no-gutters">
            {!showBlockUi && (
              <Fragment>
                <Form className="form" onSubmit={props.handleSubmit(submitChanges)}>
                  <div role="tablist" data-e2e="page-nav" className="ct-edit-my-video-page__nav">
                    <VideoTab
                      id="video-metadata-tab"
                      aria-controls="video-metadata-container"
                      ref={metadataTabRef}
                      active={showForm}
                      data-e2e="metadata-tab"
                      tabIndex={showThumbnail ? -1 : undefined}
                      onClick={showThumbnail ? switchTabs : noop}
                      onKeyDown={handleKeyDown}
                    >
                      {t('metadata')}
                    </VideoTab>

                    <VideoTab
                      id="video-thumbnail-tab"
                      aria-controls="video-thumbnail-container"
                      ref={thumbnailTabRef}
                      active={showThumbnail}
                      data-e2e="thumbnail-tab"
                      tabIndex={showForm ? -1 : undefined}
                      disabled={!thumbnailUrl}
                      onClick={showForm ? switchTabs : noop}
                      onKeyDown={handleKeyDown}
                    >
                      {t('thumbnail')}
                    </VideoTab>
                  </div>

                  <div
                    id="video-metadata-container"
                    role="tabpanel"
                    aria-labelledby="video-metadata-tab"
                    className={'ct-edit-my-video-page__body' + (!showForm ? ' ct-edit-my-video-page__body--hide' : '')}
                  >
                    <FormFields videoFormConfig={videoFormConfig} formValues={formValues} />
                    <FormControlsDecorated />
                  </div>
                </Form>

                <div
                  id="video-thumbnail-container"
                  role="tabpanel"
                  aria-labelledby="video-thumbnail-tab"
                  className={
                    'ct-edit-my-video-page__body' + (!showThumbnail ? ' ct-edit-my-video-page__body--hide' : '')
                  }
                  data-e2e="tab-content"
                >
                  <ThumbnailDecorated video={myVideo} />
                </div>
              </Fragment>
            )}
          </div>
          <GenerateThumbnailDialog video={myVideo} />
          <BlockUi show={showBlockUi}>
            <LoadingSpinner />
          </BlockUi>
        </div>
      </div>
    </PageDecorated>
  );
};

export const EditMyVideoPageDecorated = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withTranslation(),
  reduxForm({
    form: EDIT_MY_VIDEO_FORM,
  })
)(EditMyVideoPage);
